/**
 * Represents Header content with title, subtitle, link and description
 *
 * @module views/components/RailTitle
 * @memberof -Common
 */
import './RailTitle.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useLocalRef } from '@ulta/core/hooks/useLocalRef/useLocalRef';
/**
  * Represents a RailTitle component
  *
  * @method
  * @param {RailTitleProps} props - React properties passed from composition
  * @returns RailTitle
  */
export const RailTitle = React.forwardRef( ( props, ref ) => {
  const {
    linkAction,
    navLink,
    title,
    subTitle,
    description
  } = props;

  const headerSectionRef = useLocalRef( { ref } );

  return (
    <div className='RailTitle'>
      <div className='RailTitle__HeaderSection'
        tabIndex={ -1 }
        ref={ headerSectionRef }
      >
        { title && (
          <div className={ 'RailTitle__HeaderSectionTitle' }>

            <Text htmlTag='h2'
              textStyle='title-6'
            >
              { title }
            </Text>
          </div>
        ) }
        { ( linkAction && linkAction.label ) &&
        <div className='RailTitle__HeaderSectionLinkWrap'>
          <Button
            likeLink
            label={ linkAction.label }
            iconImage={ props.iconImage }
            onClick={ props.handleLink }
            iconRight={ props.iconRight }
            className='RailTitle__HeaderSectionLink'
            action={ linkAction }
          />
        </div>
        }
        { ( navLink && navLink.label ) &&
          <div className='RailTitle__HeaderSectionLinkWrap'>
            <Link_Huge
              action={ navLink }
              primary
              withHover
            >
              <Text
                htmlTag='span'
                textStyle='body-2'
              >
                { navLink.label }
              </Text>
            </Link_Huge>
          </div>
        }
      </div>
      { ( subTitle || description ) &&
      <div className='RailTitle__DetailSection'>
        { subTitle && (
          <div className='RailTitle__DetailSectionSubtitle'>
            <Text htmlTag='p'
              textStyle='body-3'
              color='neutral-600'
            >
              { subTitle }
            </Text>
          </div>
        ) }
        { description && (
          <div className='RailTitle__DetailSectionDescription'>
            <Text htmlTag='p'
              textStyle='body-3'
              color='neutral-600'
            >
              { description }
            </Text>
          </div>
        ) }
      </div>
      }
    </div>
  );
} );

/**
  * Property type definitions
  *
  * @typedef RailTitleProps
  * @type {object}
  * @property {string} title - section heading, required
  * @property {string} subTitle - section subHeading
  * @property {object} linkAction - link attibutes
  * @property {object} navLink - link attibutes as a link
  * @property {string} description - section description
  */
export const propTypes =  {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  description: PropTypes.string,
  linkAction: PropTypes.shape( {
    label: PropTypes.string,
    iconImage: PropTypes.string,
    handleLink: PropTypes.func,
    iconRight: PropTypes.bool
  } ),
  navLink: PropTypes.object
};

/**
  * Default values for passed properties
  *
  * @type {object}
  * @property {string} title - 'Title'
  * @property {string} subTitle - 'SubHeading'
  * @property {object} linkAction - 'link attibutes'
  * @property {object} navLink - 'link attibutes as a link'
  */
export const defaultProps =  {
  title: '',
  subTitle: '',
  description: '',
  linkAction: null,
  navLink: null
};

RailTitle.propTypes = propTypes;
RailTitle.defaultProps = defaultProps;
RailTitle.displayName = 'RailTitle';

export default RailTitle;